import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchInventorySummaryApi,
  fetchInventoryDetailApi,
  updateInventoryTransactionsApi,
} from "../services/inventorymanagement.api";

interface PurchaseRequest {
  id?: number;
  quantityPurchased: number;
  unitPrice: number;
  purchaseDate: string;
  invoiceRef: string;
  unit: string;
}

interface UpdateInventoryTransactionsRequest {
  productId: number;
  purchases: PurchaseRequest[];
}

// Updated interfaces to match actual API responses
interface InventorySummaryItem {
  productId: number;
  productName: string;
  currentStock: number;
  unit: string;
  lastPurchaseDate: string;
  lastApplicationDate: string;
  totalPurchased: number;
  totalApplied: number;
}

// Updated detail interfaces to match actual API response
interface ConsumptionDetail {
  id: number;
  quantityApplied: number;
  unit: string;
  appliedDate: string;
  fieldName: string;
  appliedPriceFIFO: number;
  stockAfterApplication: number;
}

interface TransactionDetail {
  id: number;
  openingStock: number;
  quantityPurchased: number;
  unit: string;
  unitPrice: number;
  purchaseDate: string;
  invoiceRef: string;
  closingStock: number;
  consumptions: ConsumptionDetail[];
}

interface InventoryDetailItem {
  productId: number;
  productName: string;
  currentStock: number;
  unit: string;
  totalPurchased: number;
  totalApplied: number;
  currentAvgCost: number;
  transactions: TransactionDetail[];
}

interface InventoryManagementState {
  inventorySummary: InventorySummaryItem[];
  inventoryDetail: InventoryDetailItem | null;
  loading: boolean;
  updating: boolean;
  loadingDetail: boolean;
  error: string | null;
  updateError: string | null;
}

const initialState: InventoryManagementState = {
  inventorySummary: [],
  inventoryDetail: null,
  loading: false,
  updating: false,
  loadingDetail: false,
  error: null,
  updateError: null,
};

export const fetchInventorySummary = createAsyncThunk(
  "inventorymanagement/summary/fetch",
  async () => {
    const response = await fetchInventorySummaryApi();
    return response.data;
  }
);

export const fetchInventoryDetail = createAsyncThunk(
  "inventorymanagement/detail/fetch",
  async (productId: number) => {
    const response = await fetchInventoryDetailApi(productId);
    return response.data;
  }
);

export const updateInventoryTransactions = createAsyncThunk(
  "inventorymanagement/transactions/update",
  async (data: UpdateInventoryTransactionsRequest, { rejectWithValue }) => {
    try {
      const response = await updateInventoryTransactionsApi(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const inventoryManagementSlice = createSlice({
  name: "inventorymanagement",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },
    clearInventoryDetail: (state) => {
      state.inventoryDetail = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch inventory summary
      .addCase(fetchInventorySummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInventorySummary.fulfilled, (state, action) => {
        state.loading = false;
        state.inventorySummary = action.payload;
      })
      .addCase(fetchInventorySummary.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch inventory summary.";
      })

      // Fetch inventory detail
      .addCase(fetchInventoryDetail.pending, (state) => {
        state.loadingDetail = true;
        state.error = null;
      })
      .addCase(fetchInventoryDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.inventoryDetail = action.payload;
      })
      .addCase(fetchInventoryDetail.rejected, (state) => {
        state.loadingDetail = false;
        state.error = "Failed to fetch inventory detail.";
      })

      // Update inventory transactions
      .addCase(updateInventoryTransactions.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateInventoryTransactions.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateInventoryTransactions.rejected, (state, action) => {
        state.updating = false;
        state.updateError = (action.payload as string) || "Failed to update inventory transactions";
      });
  },
});

export const {
  clearError,
  clearInventoryDetail,
} = inventoryManagementSlice.actions;

// Selectors
export const selectInventorySummary = (state: {
  inventorymanagement: InventoryManagementState;
}) => state.inventorymanagement.inventorySummary;

export const selectInventoryDetail = (state: {
  inventorymanagement: InventoryManagementState;
}) => state.inventorymanagement.inventoryDetail;

export const selectLoading = (state: { inventorymanagement: InventoryManagementState }) =>
  state.inventorymanagement.loading;

export const selectLoadingDetail = (state: { inventorymanagement: InventoryManagementState }) =>
  state.inventorymanagement.loadingDetail;

export const selectUpdating = (state: { inventorymanagement: InventoryManagementState }) =>
  state.inventorymanagement.updating;

export const selectError = (state: { inventorymanagement: InventoryManagementState }) =>
  state.inventorymanagement.error;

export const selectUpdateError = (state: {
  inventorymanagement: InventoryManagementState;
}) => state.inventorymanagement.updateError;

export default inventoryManagementSlice.reducer;